/**
 * Shared audio state: one AudioContext, one volume, and an honest answer to
 * "will a sound alert actually be heard?".
 *
 * Two problems this solves.
 *
 * **Volume was unsettable.** `notificationSound.ts` and `ringSound.ts` each
 * created their own `AudioContext` and hardcoded a gain (0.3 and 0.4). A
 * clinic wanting the queue ring louder than the payment beep, or wanting
 * either quieter, had nothing to turn.
 *
 * **Blocked audio failed silently.** Browsers refuse to start an AudioContext
 * until the user has interacted with the page; until then it sits `suspended`
 * and every `start()` is discarded without an error. A front desk relying on
 * the ring to know a patient is waiting would simply never hear it, with
 * nothing on screen saying so. `audioBlocked()` reports that state so the UI
 * can warn, and `unlockAudio()` resumes the context from a real user gesture.
 */

let ctx: AudioContext | null = null;
let volume = 0.6;

const listeners = new Set<() => void>();

export function getAudioContext(): AudioContext | null {
  if (!ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** `true` when the browser has not yet permitted audio to play. */
export function audioBlocked(): boolean {
  const c = getAudioContext();
  if (!c) return true;
  return c.state === 'suspended';
}

/** Must be called from a real user gesture (click/keypress) to have effect. */
export async function unlockAudio(): Promise<boolean> {
  const c = getAudioContext();
  if (!c) return false;
  try {
    if (c.state === 'suspended') await c.resume();
  } catch {
    return false;
  }
  notify();
  return c.state === 'running';
}

/** 0–1, clamped. Applied by every tone this app plays. */
export function setVolume(v: number) {
  volume = Math.min(1, Math.max(0, v));
  notify();
}

export function getVolume(): number {
  return volume;
}

/** Peak gain for a tone, scaled by the user's volume. */
export function scaledGain(base: number): number {
  return base * volume;
}

export function subscribeAudioState(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  listeners.forEach((fn) => fn());
}
